import { MantineSelectableTable } from "@/components/molecule/mantineSelectableTable/MantineSelectableTable";
import { useLocalStorage } from "@/context/LocalStorageProvider";
import { useNonStandardFetch } from "@/fetch/page/nonStandard/useNonStandardFetch";
import { useStatusHandler } from "@/hooks/handler/useStatusHandler";
import { useEquipmentStore } from "@/store/equipment.store";
import { useNonStandardStore } from "@/store/nonStandard.store";
import { useSocketStore } from "@/store/socket.store";
import { useWorkDataStore } from "@/store/work.store";
import { setDateFormatStringWithTime } from "@/utils/timeUtil";

import { getWorkTrackingStatus } from "@/utils/tranferWorkStatus";
import { Button, NumberInput, Text } from "@mantine/core";
import {
  ProductionPlansGet200ResponseRowsInnerWorksInner,
  WorksItemGet200ResponseRowsInner,
  WorksItemGet200ResponseRowsInnerTrackingStatusEnum,
} from "@sizlcorp/sizl-api-document/dist/models";
import { MRT_ColumnDef, MRT_SortingState } from "mantine-react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import { useModal } from "@/context/ModalStackManager";
import { NotificationForm } from "@/components/organism/form/NotificationForm";
import { NavLink, useNavigate } from "react-router-dom";

export interface ItemType extends Partial<WorksItemGet200ResponseRowsInner> {
  seq?: number;
  equipmentType: string;
  end: string;
  todoQuantity: number;
  defectCount: number;
}

const NonStandardTable = () => {
  const { openModal } = useModal();
  const { setWorkId, workId } = useLocalStorage();
  const navigate = useNavigate();
  const { setSelectedRowIds, setData, data, updateRow, selectedRowIds } =
    useNonStandardStore();
  const { workData } = useWorkDataStore((state) => ({
    workData: state.workData,
  }));

  const { equipment } = useEquipmentStore((state) => ({
    equipment: state.Equipment,
  }));
  const { data: nonStandardData } = useNonStandardFetch({
    workData: workData.work as ProductionPlansGet200ResponseRowsInnerWorksInner,
  });

  const { handleStart, handleEnd, handleResume } = useStatusHandler();
  const { getData } = useSocketStore((state) => ({ getData: state.getData }));
  const counterData = getData(equipment?.code as string);

  // 이전 선택 상태를 저장할 ref
  const prevSelectedRowIdsRef = useRef<number[]>([]);

  const columns = useMemo<MRT_ColumnDef<ItemType>[]>(
    () => [
      {
        accessorKey: "seq",
        header: "순번",
      },
      {
        accessorKey: "Dong",
        header: "동",
      },
      {
        accessorKey: "itemName",
        header: "제품명",
        Cell: ({ row }) => {
          const { id, itemName, equipment } = row.original;

          const handleNavigate = () => {
            setWorkId(id);
          };

          return equipment?.equipmentType === "PRESS" ? (
            <NavLink to={`/work/${id}`}>
              <Text underline c={"red"} fw={"bold"} onClick={handleNavigate}>
                {itemName}
              </Text>
            </NavLink>
          ) : (
            <div>{itemName}</div>
          );
        },
      },
      {
        accessorKey: "spec",
        header: "규격",
      },
      {
        accessorKey: "PDRev",
        header: "생산치수",
      },
      {
        accessorKey: "quantityPerFloor",
        header: "1개층",
        Cell: ({ row }) => {
          const { ProdQty, Floor } = row.original;
          const quantityPerFloor = ProdQty
            ? Math.ceil(Number(ProdQty) / Number(Floor))
            : null;
          return <div>{quantityPerFloor ?? "N/A"}</div>;
        },
      },
      {
        accessorKey: "Floor",
        header: "층별",
      },
      {
        accessorKey: "ProdQty",
        header: "총수량",
      },
      {
        accessorKey: "Angle",
        header: "각도",
      },
      {
        accessorKey: "targetQuantity",
        header: "목표 생산량",
      },
      // {
      //   accessorKey: "counter",
      //   header: "카운터",
      //   Cell: ({ row }) => {
      //     const { equipment, trackingStatus, finalEquipmentCounter } =
      //       row.original;

      //     let count;
      //     if (trackingStatus === "DONE") {
      //       count = finalEquipmentCounter ?? 0;
      //     } else if (trackingStatus === "PAUSED") {
      //       count = finalEquipmentCounter ?? 0;
      //     } else if (trackingStatus === "WORKING") {
      //       count = Number(counterData?.actualProduction ?? 0);
      //     }

      //     return <div>{equipment?.equipmentType === "PRESS" && count}</div>;
      //   },
      // },
      {
        accessorKey: "performance",
        header: "등록수량",
        Cell: ({ row }) => {
          const { summary, targetQuantity } = row.original;
          const performance = summary?.performance;
          return <div>{performance}</div>;
        },
      },
      {
        accessorKey: "end",
        header: "실적 수량",
        minSize: 100,
        Cell: (value) => {
          const { targetQuantity, id, summary, end } = value.row.original;

          // 로컬스토리지에 저장된 값 우선 사용 (사용자 수정 유지)
          let savedEnd: number | undefined = undefined;
          try {
            const raw = window.localStorage.getItem(`tempWork_${id}`);
            if (raw) {
              const parsed = JSON.parse(raw);
              const v = parsed?.end;
              if (v !== undefined && v !== null && v !== "") {
                const num = Number(v);
                if (!Number.isNaN(num)) savedEnd = num;
              }
            }
          } catch {}

          // 초기값: 목표생산량 - 등록수량(performance)
          const initialDefault = Math.max(
            0,
            Number(targetQuantity ?? 0) - Number(summary?.performance ?? 0),
          );

          const inputValue =
            savedEnd ??
            (end !== undefined && end !== null && end !== ""
              ? Number(end)
              : initialDefault);

          const onChange = (val: number | string) => {
            const numVal = typeof val === "string" ? Number(val) : val;
            if (Number.isNaN(Number(numVal))) return;
            updateRow(id as number, { end: Number(numVal) });
            // 사용자 입력을 로컬스토리지에 저장하여 새로고침 후에도 유지
            try {
              const raw = window.localStorage.getItem(`tempWork_${id}`);
              const obj = raw ? JSON.parse(raw) : {};
              const newObj = { ...obj, end: Number(numVal) };
              window.localStorage.setItem(
                `tempWork_${id}`,
                JSON.stringify(newObj),
              );
            } catch {}
          };

          return (
            <div
              onMouseDownCapture={(e) => e.stopPropagation()}
              onClickCapture={(e) => e.stopPropagation()}
              onWheelCapture={(e) => e.stopPropagation()}
              onFocusCapture={(e) => e.stopPropagation()}
              onKeyDownCapture={(e) => e.stopPropagation()}
            >
              <NumberInput
                min={0}
                onChange={onChange}
                disabled={value.row.original.trackingStatus === "WAITING"}
                value={Number(inputValue)}
              />
            </div>
          );
        },
      },
      {
        accessorKey: "defectCount",
        header: "불량 수량",
        Cell: (value) => {
          const {
            summary,
            targetQuantity,
            trackingStatus,
            finalEquipmentCounter,
          } = value.row.original;

          const calculateDefectCount = () => {
            let count;
            if (trackingStatus === "DONE" || trackingStatus === "PAUSED") {
              count = finalEquipmentCounter;
            } else if (trackingStatus === "WORKING") {
              count = Number(counterData?.actualProduction ?? 0);
            }
            return Math.max(0, Number(count) - Number(targetQuantity));
          };

          const defectCount = calculateDefectCount();

          return (
            <div>
              {defectCount > 0
                ? defectCount
                : summary?.defectTotal || undefined}
            </div>
          );
        },
      },
      {
        accessorKey: "trackingStatus",
        header: "상태",
        Cell: (value) => {
          const trackingStatus = value.row.original
            .trackingStatus as WorksItemGet200ResponseRowsInnerTrackingStatusEnum;
          return <div>{getWorkTrackingStatus(trackingStatus).status}</div>;
        },
      },
      {
        accessorKey: "endTime",
        header: "작업종료시간",
        Cell: ({ row }) => {
          const { trackingStatus, id, updatedAt, summary, targetQuantity } =
            row.original;
          const handleWorkDone = () => {
            handleEnd({
              workId: id as number,
              worksWorkIdTrackingStartPostRequest: {
                workId: id as number,
                finalEquipmentCounter: counterData?.actualProduction ?? "0",
              },
            });
          };

          const renderEndTimeButton = () => {
            if (trackingStatus === "WORKING") {
              // 조건: 등록수량(performance) + 실적수량(입력 end) >= 목표수량(targetQuantity)
              const performance = Number(summary?.performance ?? 0);
              const target = Number(targetQuantity ?? 0);

              // 입력된 end 값 계산
              // 우선순위: 로컬스토리지 값 -> row.end -> 초기값(목표-등록)
              let savedEnd: number | undefined = undefined;
              try {
                const raw = window.localStorage.getItem(`tempWork_${id}`);
                if (raw) {
                  const parsed = JSON.parse(raw);
                  if (Object.prototype.hasOwnProperty.call(parsed, "end")) {
                    const v = parsed?.end;
                    if (v !== undefined && v !== null && v !== "") {
                      const num = Number(v);
                      if (!Number.isNaN(num)) savedEnd = num;
                    } else {
                      // 명시적으로 0 입력된 경우 처리
                      savedEnd = 0;
                    }
                  }
                }
              } catch {}

              const hasStoreEnd =
                row.original.end !== undefined && row.original.end !== null;
              const storeEnd = hasStoreEnd
                ? Number(row.original.end)
                : undefined;
              const initialDefault = Math.max(
                0,
                Number(targetQuantity ?? 0) - Number(summary?.performance ?? 0),
              );
              const effectiveEnd =
                savedEnd ??
                (storeEnd !== undefined ? storeEnd : initialDefault);

              const canEnd = performance + Number(effectiveEnd) >= target;

              return (
                <Button
                  color="red"
                  variant="outline"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (canEnd) {
                      handleWorkDone();
                    } else {
                      openModal(
                        <NotificationForm
                          message={
                            "등록수량 + 실적수량이 목표수량보다 작아 종료 요청을 하지는 않습니다."
                          }
                        />,
                        "",
                        "작업종료",
                      );
                    }
                  }}
                >
                  {"작업종료"}
                </Button>
              );
            } else if (trackingStatus === "DONE") {
              return setDateFormatStringWithTime(updatedAt);
            } else {
              return (
                <Button
                  color="red"
                  disabled
                  variant="outline"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {"작업종료"}
                </Button>
              );
            }
          };

          return <div>{renderEndTimeButton()}</div>;
        },
      },
    ],
    [counterData],
  );

  useEffect(() => {
    if (nonStandardData?.data.rows) {
      if (nonStandardData?.data.rows.length === 1) {
        navigate(`/work/${workId}`);
      }

      const convertedData = nonStandardData.data.rows.map((item, index) => ({
        ...item,
        seq: index + 1,
      }));

      setData(convertedData as ItemType[]);
    }
  }, [nonStandardData]);

  useEffect(() => {
    const newSelectedRowIds = selectedRowIds?.filter(
      (id) => !prevSelectedRowIdsRef.current.includes(id),
    );

    newSelectedRowIds?.forEach((id) => {
      const selectedRow = data.find((row) => row.id === id);
      if (selectedRow) {
        const IsWorkOrderInProgress =
          selectedRow.equipment?.activeWorkId !== selectedRow.id;

        if (selectedRow.trackingStatus === "WAITING") {
          handleStart({
            workId: selectedRow.id as number,
            IsWorkOrderInProgress: IsWorkOrderInProgress,
          });
        } else if (selectedRow.trackingStatus === "PAUSED") {
          handleResume({
            workId: selectedRow.id as number,
            IsWorkOrderInProgress: IsWorkOrderInProgress,
          });
        }
      }
    });

    // 현재 선택 상태를 이전 상태로 업데이트
    prevSelectedRowIdsRef.current = selectedRowIds ?? [];
  }, [selectedRowIds]);

  useEffect(() => {
    if (counterData) {
      data.forEach((row) => {
        // PRESS 장비에 대해서만 defectCount를 업데이트하는 경우
        if (row.equipment?.equipmentType === "PRESS") {
          const newDefectCount =
            Number(counterData.actualProduction ?? 0) -
            Number(row.targetQuantity ?? 0);

          if (newDefectCount > 0) {
            updateRow(row.id as number, { defectCount: newDefectCount });
          }
        }
      });
    }
  }, [counterData]);

  useEffect(() => {
    return () => {
      setSelectedRowIds([]);
    };
  }, []);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);

  const handleSortingChange = (columnId: string) => {
    setSorting((prev) => {
      const existing = prev.find((s) => s.id === columnId);

      if (!existing) {
        return [...prev, { id: columnId, desc: false }];
      }

      if (!existing.desc) {
        return prev.map((s) => (s.id === columnId ? { ...s, desc: true } : s));
      }

      return prev.filter((s) => s.id !== columnId);
    });
  };

  return (
    <MantineSelectableTable
      maxHeight="30rem"
      columns={columns}
      data={data ?? []}
      shouldClearSelection={
        selectedRowIds !== null && selectedRowIds.length === 0
      }
      enableSorting={true}
      enableRowSelection={() => true}
      enableMultiRowSelection={true}
      onRowSelection={(rows) => {
        setSelectedRowIds(rows?.map((row) => row.id) as number[]);
      }}
      sorting={sorting}
      mantineTableHeadCellProps={({ column }) => ({
        onClick: () => {
          handleSortingChange(column.id);
        },
        sx: {
          backgroundColor: "#161B21",
          "& .mantine-Checkbox-input": {
            display: "none",
          },
        },
      })}
      mantineTableBodyCellProps={(data) => {
        const { trackingStatus } = data.row.original ?? {};
        const isDone = trackingStatus === "DONE";
        return {
          style: {
            backgroundColor: isDone ? "#068a23" : "#161B21",
            color: "#FFFFFF",
          },
        };
      }}
    />
  );
};

export default NonStandardTable;
